// MaterialCard — the created-material card across its three moments, pure.
//
// chat-as-live-work-surface §Axis 2 (the keystone): a material the agent
// created is a first-class, clickable, findable object — never a raw UUID in
// prose. The card owns THREE moments of ONE artifact (material-streaming
// 2026-09-01), carried as a typed discriminated `MaterialCardState` (a `type`
// discriminant, never booleans):
//
//   • BUILDING — a skeleton outline that fills in as the generation streams
//     (folded `BuildState`): visible Stop, honest rotating phase (the clock is the live line's).
//   • SETTLED  — the pointer card: kind glyph + title + item-count; the WHOLE
//     card opens it (no separate "Open" button, G14). The build has dissolved
//     into the artifact.
//   • STOPPED  — the author hit Stop (or the turn failed): the partial is named,
//     the card offers "Try again", never a dead spinner.
//
// The BUILDING / STOPPED states read the SAME `buildId`-keyed fold the live pane
// reads (materialBuildModel) — two viewports over one state. The SETTLED state is
// the durable pointer projected from the `material_created` marker; the transient
// build is SUPERSEDED by it (same as the final message supersedes text deltas).
//
// Pure/props-driven: the state sum in, the intents (`onOpen` / `onStop` /
// `onOpenPane` / `onRetry`) report UP. No state, no I/O. Skeleton rows are
// aria-hidden; ONE live region announces the build STATE at the fold boundary.

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import {
    type BuildPhase,
    type BuildState,
    isSlow,
    type LiveSlot,
    type QualityBand,
    questionCounts,
} from "../../../app/materialBuildModel.js";
import { AiMarker } from "../../AiMarker/AiMarker.js";
import { Badge } from "../../Badge/Badge.js";
import { Button } from "../../Button/Button.js";
import type { IconName } from "../../Icon/Icon.js";
import { Icon } from "../../Icon/Icon.js";
import { Skeleton } from "../../Skeleton/Skeleton.js";
import { Spinner } from "../../Spinner/Spinner.js";
import { Text } from "../../Text/Text.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import type { MaterialKind } from "../types.js";
import "./MaterialCard.css";

// === The state sum — one moment per variant, discriminated ====================

// BUILDING (or its lead-in `planning`): the live outline fills row by row.
export type MaterialCardBuilding = {
    readonly type: "building";
    // The folded live state (status "planning" | "building").
    readonly build: BuildState;
    // The stamped elapsed read (last delta's atMs) — never a clock.
    readonly elapsedMs: number;
    // Cancel the turn (optimistic — the card flips STOPPED at once). Offered
    // only when wired (no dead button).
    readonly onStop?: () => void;
    // Raise the live pane (the watch surface). Offered only when wired.
    readonly onOpenPane?: () => void;
};

// SETTLED: the durable pointer card (#27) — kind + title + count; the whole
// card is the open target.
export type MaterialCardSettled = {
    readonly type: "settled";
    readonly title: string;
    readonly kind: MaterialKind;
    // Authored-item count ("· 3 questions"). Absent ⇒ title + kind alone.
    readonly itemCount?: number;
    // PRIMARY: open the material as a focused tab.
    readonly onOpen: () => void;
};

// STOPPED: the partial is named; the card offers a re-run, never a dead spinner.
export type MaterialCardStopped = {
    readonly type: "stopped";
    readonly build: BuildState;
    readonly onRetry?: () => void;
};

export type MaterialCardState =
    | MaterialCardBuilding
    | MaterialCardSettled
    | MaterialCardStopped;

export type MaterialCardProps = {
    readonly state: MaterialCardState;
    readonly className?: string;
};

// The kind's leading glyph — the per-material scent read at a glance. Exhaustive
// over the sum; a new kind fails to compile until it earns a glyph.
const kindGlyph = (kind: MaterialKind): IconName => {
    switch (kind) {
        case "course":
            return "mcq";
        case "freeform":
            return "paragraph";
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

// The kind's localised LABEL — a match so a new kind must add its phrase.
const KindLabel = ({ kind }: { readonly kind: MaterialKind }): ReactNode => {
    switch (kind) {
        case "course":
            return <Trans>Course material</Trans>;
        case "freeform":
            return <Trans>Notes</Trans>;
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

// The honest sub-status line — the tool's REAL current phase, localised. NOT a
// canned carousel: each label is the phase the fold actually holds, and the
// drafting phase carries the live question tally.
const PhaseLabel = ({ build }: { readonly build: BuildState }): ReactNode => {
    const { filled, total } = questionCounts(build);
    const phase: BuildPhase = build.phase;
    switch (phase) {
        case "planning":
            return <Trans>Planning the outline…</Trans>;
        case "drafting":
            return total === 0 ? (
                <Trans>Drafting…</Trans>
            ) : (
                <Trans>
                    Drafting question {Math.min(filled + 1, total)} of {total}…
                </Trans>
            );
        case "grounding":
            return <Trans>Checking sources…</Trans>;
        case "finalizing":
            return <Trans>Finishing up…</Trans>;
        default: {
            const exhaustive: never = phase;
            return exhaustive;
        }
    }
};

// The quality VERDICT band (F37b: the Quality→Chat seam echo, `build_evaluated`
// → `BuildState.verdict`) as a glanceable badge. Colour is never the sole
// channel (WCAG SC 1.4.1) — every status tone pairs its icon with the word.
// The seam is tolerant (Quality can grow measures freely); this reads only
// `overall`, exactly as the seam contract intends.
const QualityBandBadge = ({
    band,
}: {
    readonly band: QualityBand;
}): ReactNode => {
    const { t } = useLingui();
    switch (band) {
        case "blocked":
            return (
                <Badge tone="danger" icon={<Icon name="danger" size="xs" />}>
                    {t({ message: "Needs fixing", context: "quality band" })}
                </Badge>
            );
        case "weak":
            return (
                <Badge tone="warning" icon={<Icon name="warning" size="xs" />}>
                    {t({ message: "Needs work", context: "quality band" })}
                </Badge>
            );
        case "adequate":
            return (
                <Badge tone="neutral">
                    {t({ message: "Sufficient", context: "quality band" })}
                </Badge>
            );
        case "strong":
            return (
                <Badge tone="success" icon={<Icon name="check" size="xs" />}>
                    {t({ message: "Strong", context: "quality band" })}
                </Badge>
            );
        default: {
            const exhaustive: never = band;
            return exhaustive;
        }
    }
};

// The newest completed question — the ONE "just changed" tint while building
// (slots fill in array order, so the last filled question is the newest). Pure.
const newestFilledQuestionId = (
    slots: readonly LiveSlot[],
): string | undefined => {
    const filled = slots.filter(
        (slot) => slot.kind === "question" && slot.status === "filled",
    );
    return filled.length === 0 ? undefined : filled[filled.length - 1]?.id;
};

// One outline row. Module → a heading anchor. Question → skeleton while pending
// / filling, real text on filled (with the agent "just changed" tint on the
// newest). No border ring; the tint is the only focal cue (reused idiom).
const SlotRow = ({
    slot,
    changed,
}: {
    readonly slot: LiveSlot;
    readonly changed: boolean;
}): ReactNode => {
    if (slot.kind === "module") {
        return (
            <div className="bilda-material-card__row bilda-material-card__row--module">
                <Icon name="heading" size="xs" />
                <Text size="sm" weight="semibold">
                    {slot.text}
                </Text>
            </div>
        );
    }
    if (slot.status === "filled") {
        return (
            <div
                className={`bilda-material-card__row bilda-material-card__row--question${changed ? " bilda-material-card__row--changed" : ""}`}
            >
                <Icon name="mcq" size="xs" />
                <Text size="sm">{slot.text}</Text>
            </div>
        );
    }
    // pending / filling — a skeleton line; `filling` gets the live shimmer cue.
    return (
        <div
            className={`bilda-material-card__row bilda-material-card__row--question bilda-material-card__row--pending${slot.status === "filling" ? " bilda-material-card__row--filling" : ""}`}
            aria-hidden="true"
        >
            <Icon name="mcq" size="xs" />
            <Skeleton variant="text" width="82%" />
        </div>
    );
};

// The building body — the filling outline + the honest status line + Stop/Open.
const BuildingBody = ({
    state,
}: {
    readonly state: MaterialCardBuilding;
}): ReactNode => {
    const { build, elapsedMs, onStop, onOpenPane } = state;
    const changedId = newestFilledQuestionId(build.slots);
    const planning = build.status === "planning";
    return (
        <>
            <div className="bilda-material-card__outline">
                {planning ? (
                    // Before the outline arrives the shape is genuinely unknown —
                    // an indeterminate placeholder, no fabricated row count.
                    <div
                        className="bilda-material-card__row bilda-material-card__row--pending"
                        aria-hidden="true"
                    >
                        <Skeleton variant="text" width="60%" />
                    </div>
                ) : (
                    build.slots.map((slot) => (
                        <SlotRow
                            key={slot.id}
                            slot={slot}
                            changed={slot.id === changedId}
                        />
                    ))
                )}
            </div>

            <div className="bilda-material-card__status">
                <Spinner size="sm" />
                <Text size="sm" tone="dim">
                    <PhaseLabel build={build} />
                </Text>
                {/* No clock of its own: the chat's live line below carries
                    the turn's clock, and two clocks disagreed. */}
                {isSlow(elapsedMs) ? (
                    <Text size="xs" tone="faint">
                        · <Trans>Taking longer than usual</Trans>
                    </Text>
                ) : null}
                {build.verdict !== undefined ? (
                    <QualityBandBadge band={build.verdict.overall} />
                ) : null}
            </div>

            {(onStop !== undefined || onOpenPane !== undefined) && (
                <div className="bilda-material-card__actions">
                    {onStop !== undefined ? (
                        <Button variant="danger" size="sm" onClick={onStop}>
                            <Trans>Stop</Trans>
                        </Button>
                    ) : null}
                    {onOpenPane !== undefined ? (
                        <Button variant="ghost" size="sm" onClick={onOpenPane}>
                            <Icon name="expand" size="xs" /> <Trans>Open</Trans>
                        </Button>
                    ) : null}
                </div>
            )}
        </>
    );
};

// The settled pointer body (#27, verbatim in register): kind label + count +
// AiMarker. The title is the ONE button, stretched over the whole card (CSS
// ::after), so the card is a single big target with the title as its name, and
// no second "Open" competes with the panel's own (G14; Bibliotek direction: no
// "Öppna" anywhere).
const SettledBody = ({
    state,
}: {
    readonly state: MaterialCardSettled;
}): ReactNode => {
    const { t } = useLingui();
    const { title, kind, itemCount, onOpen } = state;
    return (
        <div className="bilda-material-card__head">
            <span className="bilda-material-card__glyph" aria-hidden="true">
                <Icon name={kindGlyph(kind)} />
            </span>
            <div className="bilda-material-card__titles">
                <Text
                    as="p"
                    size="md"
                    weight="semibold"
                    className="bilda-material-card__title"
                >
                    <button
                        type="button"
                        title={title}
                        className="bilda-material-card__open"
                        onClick={onOpen}
                    >
                        {title}
                    </button>
                </Text>
                <div className="bilda-material-card__meta">
                    <Badge tone="neutral">
                        <KindLabel kind={kind} />
                    </Badge>
                    {itemCount !== undefined ? (
                        <Text size="xs" tone="faint">
                            <Plural
                                value={itemCount}
                                one="# question"
                                other="# questions"
                            />
                        </Text>
                    ) : null}
                    {/* State the AI-ness with a WORD, not colour alone
                            (ai-transparency §2#2). Agent-authored by
                            construction — the agent created it via a tool. */}
                    <AiMarker
                        variant="tag"
                        title={t`AI-generated — review before you rely on it.`}
                    />
                </div>
            </div>
        </div>
    );
};

// The stopped body — the partial is named, a re-run offered, never a spinner.
const StoppedBody = ({
    state,
}: {
    readonly state: MaterialCardStopped;
}): ReactNode => {
    const saved = questionCounts(state.build).filled;
    const verdict = state.build.verdict;
    return (
        <div className="bilda-material-card__closed">
            <Text size="sm" tone="dim">
                <Trans>
                    Stopped —{" "}
                    <Plural
                        value={saved}
                        one="# question saved"
                        other="# questions saved"
                    />
                </Trans>
            </Text>
            {verdict !== undefined ? (
                <QualityBandBadge band={verdict.overall} />
            ) : null}
            {state.onRetry !== undefined ? (
                <Button variant="secondary" size="sm" onClick={state.onRetry}>
                    <Trans>Try again</Trans>
                </Button>
            ) : null}
        </div>
    );
};

// The one accessible announcement — the STATE, once, at the fold boundary; never
// per slot, never per elapsed tick.
const LiveAnnouncement = ({
    state,
}: {
    readonly state: MaterialCardState;
}): ReactNode => {
    const { t } = useLingui();
    const message =
        state.type === "settled"
            ? t`The material is ready.`
            : state.type === "stopped"
              ? t`The build was stopped.`
              : t`Building the material…`;
    return (
        <VisuallyHidden as="div" role="status" aria-live="polite">
            {message}
        </VisuallyHidden>
    );
};

export const MaterialCard = ({
    state,
    className,
}: MaterialCardProps): ReactNode => {
    const { t } = useLingui();
    return (
        // biome-ignore lint/a11y/useSemanticElements: group (not region): many cards share this name, and same-named landmarks violate landmark-unique
        <section
            role="group"
            className={`bilda-material-card${className ? ` ${className}` : ""}`}
            aria-label={t`Created material`}
        >
            {state.type === "building" && (
                <>
                    <div className="bilda-material-card__buildhead">
                        <span
                            className="bilda-material-card__glyph"
                            aria-hidden="true"
                        >
                            <Icon name="material" size="sm" />
                        </span>
                        {/* Before the outline names it, a neutral noun, never
                            the phase word: "Drafting…" here doubled the status
                            line and read as a clipped title (G9). */}
                        <Text
                            size="md"
                            weight="semibold"
                            className="bilda-material-card__title"
                        >
                            {state.build.title ?? t`New material`}
                        </Text>
                        <AiMarker variant="tag" />
                    </div>
                    <BuildingBody state={state} />
                </>
            )}
            {state.type === "settled" && <SettledBody state={state} />}
            {state.type === "stopped" && (
                <>
                    <div className="bilda-material-card__buildhead">
                        <span
                            className="bilda-material-card__glyph"
                            aria-hidden="true"
                        >
                            <Icon name="material" size="sm" />
                        </span>
                        <Text
                            size="md"
                            weight="semibold"
                            className="bilda-material-card__title"
                        >
                            {state.build.title ?? t`Draft`}
                        </Text>
                        <AiMarker variant="tag" />
                    </div>
                    <StoppedBody state={state} />
                </>
            )}
            <LiveAnnouncement state={state} />
        </section>
    );
};
